import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';

// Landing Page
import LocalBrandingProLanding from './components/LocalBrandingProLanding';

// Industry & Blog Pages
import IndustryLandingPage from './components/IndustryLandingPage';
import LocationLandingPage from './components/LocationLandingPage';
import WarmHandoffsLanding from './components/WarmHandoffsLanding';
import LanguagePrompt from './components/LanguagePrompt';
import BlogPage from './components/BlogPage';
import ComparisonBrightLocal from './components/ComparisonBrightLocal';
import ComparisonVendasta from './components/ComparisonVendasta';
import ComparisonGoHighLevel from './components/ComparisonGoHighLevel';
import ComparisonYext from './components/ComparisonYext';
import ComparisonHub from './components/ComparisonHub';
import BlogArticle from './components/BlogArticle';

// Geo SEO Pages
import GeoServicePage from './components/GeoServicePage';
import { LocationsIndex, StateLocations, CityLocations } from './components/GeoLocations';

// SEO Meta Manager
import RouteMetaManager from './components/RouteMetaManager';

// Admin
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';

// Client Dashboard
import ClientLogin from './components/client-dashboard/ClientLogin';
import ClientDashboard from './components/client-dashboard/ClientDashboard';
import ClientOnboardingForm from './components/client-dashboard/ClientOnboardingForm';

// Signup Flow
import SignupPage from './components/SignupPage';
import SignupSuccess from './components/SignupSuccess';

// Legal / Compliance Pages
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import SMSCompliance from './components/SMSCompliance';

import './index.css';

const GA_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-B11SD8BN66';

function GAPageTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', GA_ID, { page_path: location.pathname + location.search });
    }
    // WTS Partner Attribution — capture ?ref= into a 30-day cookie on every page
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `wts_ref=${encodeURIComponent(ref)};path=/;expires=${expires};SameSite=Lax`;
    }
  }, [location]);
  return null;
}

function App() {
  // Runtime GA4 injection — failsafe if index.html tag is missing/cached
  useEffect(() => {
    if (typeof window.gtag === 'function') return;
    const s1 = document.createElement('script');
    s1.async = true;
    s1.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    document.head.appendChild(s1);
    const s2 = document.createElement('script');
    s2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments)}gtag('js',new Date());gtag('config','${GA_ID}');`;
    document.head.appendChild(s2);
  }, []);
  return (
    <BrowserRouter>
      <GAPageTracker />
      <RouteMetaManager />
      <LanguagePrompt />
      <Routes>
        {/* Marketing Landing Page */}
        <Route path="/" element={<LocalBrandingProLanding />} />

        {/* Industry Landing Pages */}
        <Route path="/industries/:slug" element={<IndustryLandingPage />} />
        <Route path="/industries/:slug/:location" element={<LocationLandingPage />} />
        <Route path="/warm-handoffs" element={<WarmHandoffsLanding />} />

        {/* Blog */}
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        {/* Comparison Pages */}
        <Route path="/compare" element={<ComparisonHub />} />
        <Route path="/compare/brightlocal" element={<ComparisonBrightLocal />} />
        <Route path="/compare/vendasta" element={<ComparisonVendasta />} />
        <Route path="/compare/gohighlevel" element={<ComparisonGoHighLevel />} />
        <Route path="/compare/yext" element={<ComparisonYext />} />

        {/* Admin Panel */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/*" element={<AdminDashboard />} />

        {/* Client Dashboard */}
        <Route path="/client/login" element={<ClientLogin />} />
        <Route path="/client/login/:slug" element={<ClientLogin />} />

        {/* Signup Flow */}
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/signup/success" element={<SignupSuccess />} />
        <Route path="/client/reset-password" element={<ClientLogin />} />
        <Route path="/client/onboarding/:accessCode" element={<ClientOnboardingForm />} />
        <Route path="/client/*" element={<ClientDashboard />} />

        {/* Legal / Compliance */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/sms-compliance" element={<SMSCompliance />} />

        {/* Geo SEO Location Pages */}
        <Route path="/locations" element={<LocationsIndex />} />
        <Route path="/locations/:stateSlug" element={<StateLocations />} />
        <Route path="/locations/:stateSlug/:citySlug" element={<CityLocations />} />
        <Route path="/locations/:stateSlug/:citySlug/:serviceId" element={<GeoServicePage />} />

        {/* Convenience redirects */}
        <Route path="/login" element={<Navigate to="/client/login" replace />} />
        <Route path="/app" element={<Navigate to="/client/login" replace />} />
        <Route path="/partner" element={<Navigate to="/warm-handoffs" replace />} />
        <Route path="/partner/hq" element={<Navigate to="/warm-handoffs" replace />} />
        <Route path="/partner/*" element={<Navigate to="/warm-handoffs" replace />} />
        <Route path="/affiliate" element={<Navigate to="/warm-handoffs" replace />} />
        <Route path="/affiliate/*" element={<Navigate to="/warm-handoffs" replace />} />

        {/* 404 */}
        <Route path="*" element={
          <div className="min-h-screen flex items-center justify-center bg-slate-100">
            <div className="text-center">
              <h1 className="text-6xl font-black text-slate-200 mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>404</h1>
              <p className="text-slate-600 mb-6">Page not found</p>
              <a href="/" className="text-blue-600 font-medium hover:underline">Back to Home</a>
            </div>
          </div>
        } />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
